import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/* ---------- helpers ---------- */
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const getId = (u) => (u && typeof u === "object" ? u._id : u) || "";
const getName = (u) =>
  (u && typeof u === "object" && (u.name || u.userId)) || "";
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString();
};
const normalizeTopics = (topics) =>
  asArray(topics)
    .map((t) => (typeof t === "object" ? t?.name || t?.title || t?._id : t))
    .filter(Boolean);

async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`
    );
  return data;
}

/* ---------- component ---------- */
export default function InductionAllPanel({
  apiBase,
  projectId,
  onBack,
  title = "All Inductions",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState({
    from: "",
    to: "",
    topic: "",
    attendee: "",
    hasGeo: "",
  });

  const abortRef = useRef(null);

  /* ---------- fetch ---------- */
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        const url = new URL(apiBase + "/induction");
        url.searchParams.set("project", projectId);
        const res = await safeFetchJSON(url.toString(), { signal: ac.signal });
        let list = Array.isArray(res) ? res : res?.data || [];

        // ensure project filter
        list = list.filter((it) => {
          const p = it?.project;
          const id = typeof p === "object" ? p?._id : p;
          return String(id || "") === String(projectId || "");
        });

        setRows(list);
      } catch (e) {
        if (e.name !== "AbortError")
          setErr(e.message || "Failed to load inductions.");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId]);

  /* ---------- owner options ---------- */
  const ownerOptions = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      for (const u of [r?.conductedBy, r?.createdby]) {
        const id = getId(u);
        if (id && !map.has(id)) map.set(id, getName(u) || "Unnamed");
      }
    }
    return [...map].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const parseBoundary = useCallback((s, end = false) => {
    if (!s) return null;
    const hasTime = /T|\d{1,2}:\d{2}/.test(s);
    const d = new Date(hasTime ? s : `${s}T${end ? "23:59:59" : "00:00:00"}`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  /* ---------- filtering & summary ---------- */
  const { filtered, presenterSummary, quickStats } = useMemo(() => {
    const qi = q.trim().toLowerCase();
    const topicNeedle = adv.topic.trim().toLowerCase();
    const attendeeNeedle = adv.attendee.trim().toLowerCase();
    const fromT = parseBoundary(adv.from)?.getTime() ?? null;
    const toT = parseBoundary(adv.to, true)?.getTime() ?? null;

    const out = [];
    const byOwner = new Map();
    let totalMeetings = 0;
    let totalAttendees = 0;

    for (const it of rows) {
      const topics = normalizeTopics(it?.topic);
      const attendees = asArray(it?.attendees);
      const hay = [
        it?.reportId,
        it?.comments,
        it?.geotagging,
        ...topics,
        ...attendees,
        getName(it?.conductedBy),
        getName(it?.createdby),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (qi && !hay.includes(qi)) continue;
      if (ownerId) {
        const ids = [getId(it?.conductedBy), getId(it?.createdby)].filter(
          Boolean
        );
        if (!ids.map(String).includes(String(ownerId))) continue;
      }

      const base = it?.date || it?.createdAt;
      const t = base ? new Date(base).getTime() : 0;
      if (fromT && (!t || t < fromT)) continue;
      if (toT && (!t || t > toT)) continue;

      if (topicNeedle && !topics.join(" ").toLowerCase().includes(topicNeedle))
        continue;
      if (
        attendeeNeedle &&
        !attendees.join(" ").toLowerCase().includes(attendeeNeedle)
      )
        continue;

      out.push(it);

      // analytics
      totalMeetings += 1;
      const headcount = Number(it?.attendeesNos) || 0;
      totalAttendees += headcount;

      // presenter aggregation
      const ownerLike = it?.conductedBy || it?.createdby || null;
      const oid = getId(ownerLike) || "__unassigned__";
      const oname =
        oid === "__unassigned__"
          ? "Unassigned"
          : getName(ownerLike) || "Unnamed";
      let ent = byOwner.get(oid);
      if (!ent) {
        ent = {
          id: oid,
          name: oname,
          totalMeetings: 0,
          totalAttendees: 0,
          dates: new Set(),
        };
        byOwner.set(oid, ent);
      }
      ent.totalMeetings += 1;
      ent.totalAttendees += headcount;
      if (it?.date) ent.dates.add(fmtDate(it.date));
    }

    const presenterSummary = [...byOwner.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    const quickStats = {
      totalMeetings,
      totalPresenters: presenterSummary.length,
      totalAttendees,
    };

    return { filtered: out, presenterSummary, quickStats };
  }, [rows, q, ownerId, adv, parseBoundary]);

  return (
    <div>
      {/* Header */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-base font-semibold">
          {title}
          <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
            {filtered.length}
          </span>
        </h4>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl bg-blue-50 p-3">
            <div className="text-gray-500 text-xs">Total Inductions</div>
            <div className="font-bold text-xl">{quickStats.totalMeetings}</div>
          </div>
          <div className="rounded-xl bg-green-50 p-3">
            <div className="text-gray-500 text-xs">Total Trainers</div>
            <div className="font-bold text-xl">
              {quickStats.totalPresenters}
            </div>
          </div>
          <div className="rounded-xl bg-orange-50 p-3">
            <div className="text-gray-500 text-xs">Total Attendees</div>
            <div className="font-bold text-xl">{quickStats.totalAttendees}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <input
            className="w-56 rounded-xl border px-3 py-2 text-sm"
            placeholder="Search reportId, topic, attendee, comments..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="rounded-xl border px-2 py-2 text-sm"
          >
            <option value="">All Trainers</option>
            {ownerOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => setAdvOpen((v) => !v)}
            className="rounded-xl border px-3 py-2 text-sm"
          >
            {advOpen ? "Close Filters" : "Advanced Filters"}
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-xl border px-3 py-2 text-sm"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div>Loading…</div>
      ) : err ? (
        <div className="text-red-600">{err}</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border shadow-sm bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-100">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <th className="px-4 py-3">Trainer</th>
                <th className="px-4 py-3">Total Inductions</th>
                <th className="px-4 py-3">Total Attendees</th>
                <th className="px-4 py-3">Dates Covered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {presenterSummary.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-indigo-50 cursor-default pointer-events-none"
                >
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {row.name}
                  </td>
                  <td className="px-4 py-3">{row.totalMeetings}</td>
                  <td className="px-4 py-3">{row.totalAttendees}</td>
                  <td className="px-4 py-3">
                    {[...row.dates].join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
